// src/App.jsx
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';

// Eager imports - cần ngay cho màn hình đầu và layout
import Login from './pages/Login';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';

// Lazy imports - mỗi page tách thành chunk riêng, chỉ tải khi truy cập
const Dashboard = lazy(() => import('./pages/Dashboard'));
const MyWork = lazy(() => import('./pages/MyWork'));
const Workload = lazy(() => import('./pages/Workload'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Users = lazy(() => import('./pages/Users'));
const Projects = lazy(() => import('./pages/Projects'));
const ProjectDashboard = lazy(() => import('./pages/ProjectDashboard'));
const ProjectEdit = lazy(() => import('./pages/ProjectEdit'));
const Tasks = lazy(() => import('./pages/Tasks'));
const TaskDetail = lazy(() => import('./pages/TaskDetail'));
const TaskEdit = lazy(() => import('./pages/TaskEdit'));
const PersonalTodos = lazy(() => import('./pages/PersonalTodos'));
const SEO = lazy(() => import('./pages/SEO'));
const SeoOverview = lazy(() => import('./pages/SeoOverview'));
const SEOEdit = lazy(() => import('./pages/SEOEdit'));
const Analytics = lazy(() => import('./pages/Analytics'));
const GA4Report = lazy(() => import('./pages/GA4Report'));
const EnhancedAnalytics = lazy(() => import('./pages/EnhancedAnalytics'));
const WebsiteOverview = lazy(() => import('./pages/WebsiteOverview'));
const Alerts = lazy(() => import('./pages/Alerts'));
const SeoHealthDashboard = lazy(() => import('./pages/SeoHealthDashboard'));
const SeoHealthDetail = lazy(() => import('./pages/SeoHealthDetail'));
const Profile = lazy(() => import('./pages/Profile'));
const MarketingDashboard = lazy(() => import('./pages/MarketingDashboard'));
const MisaCommandCenter = lazy(() => import('./pages/MisaCommandCenter'));
const MarketingBonusManagement = lazy(() => import('./pages/MarketingBonusManagement'));
const MarketingBonusWorkspace = lazy(() => import('./pages/MarketingBonusWorkspace'));
const MarketingBonusTeams = lazy(() => import('./pages/MarketingBonusTeams'));
const MarketingBonusTeamDetail = lazy(() => import('./pages/MarketingBonusTeamDetail'));
const MarketingBonusRevenue = lazy(() => import('./pages/MarketingBonusRevenue'));
const MarketingBonusOverview = lazy(() => import('./pages/MarketingBonusOverview'));
const MarketingBonusHistory = lazy(() => import('./pages/MarketingBonusHistory'));
const MarketingBonusKPI = lazy(() => import('./pages/MarketingBonusKPI'));
const MarketingBonusCalculator = lazy(() => import('./pages/MarketingBonusCalculator'));
const MarketingBonusKPIEvaluation = lazy(() => import('./pages/MarketingBonusKPIEvaluation'));
const MarketingBonusIndividual = lazy(() => import('./pages/MarketingBonusIndividual'));
const KPIAssignments = lazy(() => import('./pages/KPIAssignments'));
const LeaderKPIReview = lazy(() => import('./pages/LeaderKPIReview'));
const FundManagement = lazy(() => import('./pages/FundManagement'));
const AIWebsiteAnalysisPage = lazy(() => import('./pages/AIWebsiteAnalysisPage'));
const ExpenseProposals = lazy(() => import('./pages/ExpenseProposals'));
const ExpenseProposalDetail = lazy(() => import('./pages/ExpenseProposalDetail'));
const SheetSourcesManagement = lazy(() => import('./pages/SheetSourcesManagement'));
const CustomerDataDashboard = lazy(() => import('./pages/CustomerDataDashboard'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const KnowledgeForm = lazy(() => import('./components/KnowledgeForm'));
const KeywordsImport = lazy(() => import('./components/KeywordsImport'));
const KnowledgeAdmin = lazy(() => import('./components/KnowledgeAdmin'));
const KeywordRepository = lazy(() => import('./components/KeywordRepository'));
const NotificationManagement = lazy(() => import('./pages/NotificationManagement'));
const SEONews = lazy(() => import('./components/SEONews'));
const EmployeeMetrics = lazy(() => import('./components/EmployeeMetrics'));
const Checklists = lazy(() => import('./components/Checklists'));
const OpenAITestPanel = lazy(() => import('./components/OpenAITestPanel'));
const UserGuide = lazy(() => import('./pages/UserGuide'));
const AccountManagement = lazy(() => import('./pages/AccountManagement'));
const MindmapPage = lazy(() => import('./pages/MindmapPage'));
const TaskPerformanceDashboard = lazy(() => import('./pages/TaskPerformanceDashboard'));

// Fallback hiển thị khi đang tải chunk của page
const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

const App = () => {
  return (
    <Router>
      <Routes>
        {/* Login Route */}
        <Route path="/login" element={<Login />} />

        {/* Admin Routes (Protected) */}
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="my-work" element={<MyWork />} />
                  <Route path="workload" element={<ProtectedRoute roles={['admin', 'leader']}><Workload /></ProtectedRoute>} />
                  <Route path="portfolio" element={<ProtectedRoute roles={['admin', 'leader']}><Portfolio /></ProtectedRoute>} />
                  <Route path="users" element={<ProtectedRoute roles={['admin']}><Users /></ProtectedRoute>} />
                  <Route path="projects" element={<Projects />} />
                  <Route path="projects/:projectId" element={<ProjectDashboard />} />
                  <Route path="projects/edit/:projectId" element={<ProjectEdit />} />
                  <Route path="tasks" element={<Tasks />} />
                  <Route path="tasks/:taskId" element={<TaskDetail />} />
                  <Route path="tasks/edit/:taskId" element={<TaskEdit />} />
                  <Route path="task-performance" element={<ProtectedRoute><TaskPerformanceDashboard /></ProtectedRoute>} />
                  <Route path="personal-todos" element={<PersonalTodos />} />
                  <Route path="seo" element={<SEO />} />
                  <Route path="seo-overview" element={<SeoOverview />} />
                  <Route path="seo/:siteId" element={<SEOEdit />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="alerts" element={<Alerts />} />
                  <Route path="notifications" element={<ProtectedRoute><NotificationManagement /></ProtectedRoute>} />
                  <Route path="seo-health" element={<SeoHealthDashboard />} />
                  <Route path="seo-health/:websiteId" element={<SeoHealthDetail />} />
                  <Route path="seo/:websiteId/ga4-report" element={<GA4Report />} />
                  <Route path="seo/:websiteId/enhanced-analytics" element={<ProtectedRoute><EnhancedAnalytics /></ProtectedRoute>} />
                  <Route path="seo/:websiteId/overview" element={<ProtectedRoute><WebsiteOverview /></ProtectedRoute>} />
                  <Route path="seo/:websiteId/ai-analysis" element={<ProtectedRoute><AIWebsiteAnalysisPage /></ProtectedRoute>} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="marketing" element={<ProtectedRoute><MarketingDashboard /></ProtectedRoute>} />
                  <Route path="marketing/command-center" element={<ProtectedRoute><MisaCommandCenter /></ProtectedRoute>} />
                  <Route path="marketing-bonus" element={<ProtectedRoute roles={['admin', 'leader']}><MarketingBonusWorkspace /></ProtectedRoute>} />
                  <Route path="marketing-bonus/manage" element={<ProtectedRoute roles={['admin']}><MarketingBonusManagement /></ProtectedRoute>} />
                  <Route path="marketing-bonus/teams" element={<ProtectedRoute><MarketingBonusTeams /></ProtectedRoute>} />
                  <Route path="marketing-bonus/teams/:teamId" element={<ProtectedRoute><MarketingBonusTeamDetail /></ProtectedRoute>} />
                  <Route path="marketing-bonus/revenue" element={<ProtectedRoute><MarketingBonusRevenue /></ProtectedRoute>} />
                  <Route path="marketing-bonus/overview" element={<ProtectedRoute><MarketingBonusOverview /></ProtectedRoute>} />
                  <Route path="marketing-bonus/history" element={<ProtectedRoute><MarketingBonusHistory /></ProtectedRoute>} />
                  <Route path="marketing-bonus/kpi" element={<ProtectedRoute><MarketingBonusKPI /></ProtectedRoute>} />
                  <Route path="marketing-bonus/calculator" element={<ProtectedRoute><MarketingBonusCalculator /></ProtectedRoute>} />
                  <Route path="marketing-bonus/evaluation" element={<ProtectedRoute><MarketingBonusKPIEvaluation /></ProtectedRoute>} />
                  <Route path="marketing-bonus/individual" element={<ProtectedRoute><MarketingBonusIndividual /></ProtectedRoute>} />
                  <Route path="kpi-assignments" element={<ProtectedRoute><KPIAssignments /></ProtectedRoute>} />
                  <Route path="my-kpi-assignments" element={<ProtectedRoute><LeaderKPIReview /></ProtectedRoute>} />
                  <Route path="kpi-assignments/:assignmentId" element={<ProtectedRoute><LeaderKPIReview /></ProtectedRoute>} />
                  <Route path="fund" element={<ProtectedRoute><FundManagement /></ProtectedRoute>} />
                  <Route path="expense-proposals" element={<ProtectedRoute><ExpenseProposals /></ProtectedRoute>} />
                  <Route path="expense-proposals/:id" element={<ProtectedRoute><ExpenseProposalDetail /></ProtectedRoute>} />
                  <Route path="google-sheets" element={<ProtectedRoute><SheetSourcesManagement /></ProtectedRoute>} />
                  <Route path="customer-data-dashboard" element={<ProtectedRoute><CustomerDataDashboard /></ProtectedRoute>} />
                  <Route path="knowledge" element={<ProtectedRoute><KnowledgeBase /></ProtectedRoute>} />
                  <Route path="knowledge/keywords" element={<ProtectedRoute><KeywordRepository /></ProtectedRoute>} />
                  <Route path="knowledge/news" element={<ProtectedRoute><SEONews /></ProtectedRoute>} />
                  <Route path="knowledge/metrics" element={<ProtectedRoute><EmployeeMetrics /></ProtectedRoute>} />
                  <Route path="knowledge/checklists" element={<ProtectedRoute><Checklists /></ProtectedRoute>} />
                  <Route path="knowledge/create" element={<ProtectedRoute><KnowledgeForm mode="create" /></ProtectedRoute>} />
                  <Route path="knowledge/edit/:id" element={<ProtectedRoute><KnowledgeForm mode="edit" /></ProtectedRoute>} />
                  <Route path="knowledge/import" element={<ProtectedRoute><KeywordsImport /></ProtectedRoute>} />
                  <Route path="knowledge/admin" element={<ProtectedRoute><KnowledgeAdmin /></ProtectedRoute>} />
                  <Route path="ai-config" element={<ProtectedRoute><OpenAITestPanel /></ProtectedRoute>} />
                  <Route path="user-guide" element={<UserGuide />} />
                  <Route path="accounts" element={<ProtectedRoute><AccountManagement /></ProtectedRoute>} />
                  <Route path="mindmap" element={<ProtectedRoute><MindmapPage /></ProtectedRoute>} />
                  {/* Nếu path /admin/ gốc, redirect sang /admin/dashboard */}
                  <Route path="" element={<Navigate to="dashboard" replace />} />
                </Routes>
                </Suspense>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Redirect bất kỳ đường dẫn khác về Login (hoặc 404) */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
